/**
 * Where an idle animation mounts its actors:
 *  - `footer`:   the persistent thin strip anchored above the footer's top line
 *    (good for ground-level parades that "walk the line").
 *  - `footer-below-calendar`: a strip on the footer's top line like `footer`,
 *    but horizontally clamped to the calendar's left/right boundaries instead of
 *    spanning the whole footer. Good for ground-level scenes that should read as
 *    a "level" contained under the calendar (e.g. the Mario-style run).
 *  - `page`:     a layer anchored to the calendar's box (a short band just below
 *    the calendar), falling back to a fixed viewport-sized layer when the
 *    calendar can't be located. Good for effects that should play over the
 *    calendar area (e.g. the space encounter).
 *  - `fullpage`: a dedicated fixed, full-viewport layer behind all content that
 *    always spans the entire page regardless of the calendar's box. Good for
 *    ambient effects like shooting stars that should use the whole background.
 *
 * The {@link IdleAnimationDirector} resolves the matching stage element and
 * passes it to {@link IdleAnimation.play}.
 */
export type IdleAnimationStage = 'footer' | 'footer-below-calendar' | 'page' | 'fullpage';

/**
 * Contract for a single decorative idle animation played inside a stage.
 *
 * The {@link IdleAnimationDirector} owns all the expensive gating (idle timer,
 * visibility, on-screen detection, reduced-motion) and plays exactly one
 * animation at a time. Each animation only supplies its own choreography: it
 * spawns its actors into the provided stage, runs a CSS-keyframe sequence, and
 * resolves once the pass is finished so the director can tear down and re-arm.
 *
 * Implementations MUST:
 *  - be CSS-keyframe driven (no continuous requestAnimationFrame render loops),
 *  - remove every DOM node they create before/when the returned promise settles,
 *  - abort promptly and clean up when the provided AbortSignal fires (the
 *    director aborts on detach or when the footer leaves the viewport).
 */
export interface IdleAnimation {
  /** Human-readable id, handy for debugging/registry logs. */
  readonly name: string;

  /** Which stage this animation mounts into (footer strip, calendar-anchored page layer, or full-viewport layer). */
  readonly stage: IdleAnimationStage;

  /**
   * Run a single pass of the animation inside {@link stage}.
   * @param stage  The resolved stage element (footer strip or full-page layer).
   * @param signal Aborts the pass; implementations should stop and clean up.
   * @returns A promise that resolves when the pass has finished (or aborted).
   */
  play(stage: HTMLElement, signal: AbortSignal): Promise<void>;
}

/**
 * Resolves when the given element fires an `animationend` for the named
 * animation, or immediately when the abort signal fires. Used by CSS-driven
 * animations to know when their crossing/finishing keyframe has completed.
 *
 * The listeners are always removed, so callers can await this without leaks.
 */
export function waitForAnimationEnd(
  el: HTMLElement,
  animationName: string,
  signal: AbortSignal
): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const cleanup = (): void => {
      el.removeEventListener('animationend', onEnd);
      signal.removeEventListener('abort', onAbort);
    };

    const onEnd = (e: AnimationEvent): void => {
      if (e.animationName !== animationName) {
        return;
      }
      cleanup();
      resolve();
    };

    const onAbort = (): void => {
      cleanup();
      resolve();
    };

    el.addEventListener('animationend', onEnd);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Resolves after `ms` milliseconds, or immediately when the abort signal fires.
 * The pending timer and abort listener are always cleared, so callers can await
 * this between animation phases without leaking timers when a pass is aborted.
 *
 * Handy for multi-phase, choreographed idle animations that need a scripted
 * pause between CSS-keyframe segments (e.g. wait a beat before the next actor
 * enters) while still cancelling promptly on the director's abort.
 */
export function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const cleanup = (): void => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    };

    const onAbort = (): void => {
      cleanup();
      resolve();
    };

    const timer = window.setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    signal.addEventListener('abort', onAbort, { once: true });
  });
}
